/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState, KeyboardEvent, useRef, useMemo } from 'react';
import moment from 'moment';
import { StyledDatePickerDiv, StyledFooterDatePickerDiv } from './NGDatepicker.styles';
import { DatePickerProps } from './NGDatepicker.types';
import {
  SHORT_WEEK_DAYS,
  LONG_WEEK_DAYS_NAME,
  SHORT_MONTH_NAME,
  SHORT_WEEK_DAYS_NAME,
  FLEXIBLE_FORMAT_YEAR_MONTH,
  MAX_NUMBER_OF_NIGHTS_ALLOWED,
} from '../../store/ngDatepickerConstants';
import {
  DropDownModal,
  getLocalizedDate,
  InputTextField,
  TabComponent,
  Button,
  getCurrentDateObject,
  getNextDateObject,
  getTotalNumberOfDays,
  getValueFromDate,
  DateObjectType,
  useCheckBreakpoint,
  Icon,
  shouldIgnoreClickOutside,
  shouldIgnoreFocusOutside,
} from '@marriott/mi-ui-library';
import clsx from 'clsx';
import DatePickerExact from './NGDatepicker.exact';
import DatePickerFlexible from './NGDatepicker.flexible';
import { useStaticDataContext } from '../../context/StaticDataContext';
import { SearchFormState, useSearchFormStore } from '../../store/searchFormStore';
import { LocaleUtils } from 'react-day-picker';
import { DATES, SPECIFIC_DATES, FLEXIBLE_DATES } from '../../store/store.constants';
import { searchFormClickTrackingLoc, datepickerResetClickTrackingLoc } from '../../utils/ngSearchConstants';
import { TH_LOCALE, EL_LOCALE, AR_LOCALE, HI_LOCALE } from '../../store/ngDatepickerConstants';
import { isCNLocale, isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import { getCustomFormattedDate } from '../../utils/searchUtils';
import { generateRandomNumber, CN_FLEXIBLE_FORMAT_YEAR_MONTH } from '@marriott/shared/mi-helper-utils';
import { useLazyQuery } from '@apollo/client';
import { phoenixShopAdvSearchInventoryDate } from './queries';
import { updateSessionInSearchForm, formatDateStringBasedOnLocale } from '../../utils/datepickerHelper';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { convertToUpperCase, modifyDateStringForThai, measureTextWidth } from '../../utils/searchUtils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { DEFAULT_GROUP_DATE_LIMIT, DEFAULT_SINGLE_DATE_LIMIT } from '../../store/ngDatepickerConstants';
import { useConditionalPageContextSERP } from '../../hooks/useConditionalPageContextSERP';

const NGDatePicker: FC<DatePickerProps> = ({
  onDatePickerChange,
  onDatePickerCancel,
  onInputClick,
  onInteraction,
  isModalOpen,
  isMobileTestView,
  alignRight = false,
  hideTabHeader = false,
  setDefaultDate = false,
  isMobileForm,
  isSearchFormSticky,
  isWeekendButtonSelected,
  setIsWeekendButtonSelected,
  modalId,
  mobileModalInputClassName,
  inputFieldWrapperClassName,
  isNextGenHP = false,
  enableWeekendSelectorOnMobile = true,
  isInModal,
  hideInput = false,
  applyTrackLabel,
}) => {
  const clickTrackingLoc = 'Search Form Dates Module';
  /**
   * search form store
   */
  const dates = useSearchFormStore((state: SearchFormState) => state.dates);
  const setSearchFormState = useSearchFormStore((state: SearchFormState) => state.setSearchFormState);
  // Get search form active state for NextGen standard styling
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isSearchFormActiveState = useSearchFormStore((state: any) => state?.isSearchFormActiveState);
  const setIsSearchFormActiveState = useSearchFormStore((state: any) => state?.setIsSearchFormActiveState);
  const { currentLocale: pageCurrentLocale, pageState, isAuthorMode } = useConditionalPageContextSERP();
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);

  const searchFormTopPosition = useSearchFormStore((state: any) => state?.searchFormTopPosition);
  const activePopup = useSearchFormStore((state: any) => state?.activePopup);
  const setActivePopup = useSearchFormStore((state: any) => state?.setActivePopup);

  /** all the data from store */
  const { fromDate, toDate, flexible: flexibleValue, numberOfNights: nightCount = 1 } = dates ?? {};
  // Handle both boolean and string values for flexible flag
  // String 'false' should be treated as false, string 'true' as true
  // The store uses string values ('true'/'false') but we need a boolean for component logic
  const flexible = String(flexibleValue) === 'true';

  const {
    exactDatesTabText,
    flexibleTabText,
    stayDates,
    dateModalHeader,
    nights,
    nightLabel,
    weekdays,
    weekdaysLong,
    months,
    weekdaysShort,
    monthsShort,
    maxNumberOfNights,
    dateHelperText,
    dateHelperTextMobile,
    enableFlexibleSearch,
    flexibleDatesToggle,
    submitSpecificDateCta,
    resetSpecificDateCta,
    flexibleDatesSubmitCta,
    submitSpecificDateCtaMobile,
    currentLocale = pageCurrentLocale,
    flexibleDateFormatLabel,
  } = useStaticDataContext();

  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  /** States */
  const [datepickerPopupOpenState, setDatepickerPopupOpenState] = useState(isModalOpen ?? false);
  const [resetFlag, setResetFlag] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  // Track if component has mounted on client to fix SSR hydration mismatch
  // SSR returns fallback width (text.length * 10), client measures actual font width
  const [isClientMounted, setIsClientMounted] = useState(false);
  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const hideFlexibleDateFlow = !enableFlexibleSearch;
  const [singleDateLimit, setSingleDateLimit] = useState(
    globalData?.sessionData?.cacheData?.data?.AriesCommon?.singleDateLimit
  );

  const [isClientSideView, setIsClientSideView] = useState(false);

  // Sync datepickerPopupOpenState with isModalOpen prop changes
  useEffect(() => {
    if (isModalOpen !== undefined) {
      setDatepickerPopupOpenState(isModalOpen);
    }
  }, [isModalOpen]);

  // Close datepicker when another popup becomes active (popup coordination)
  useEffect(() => {
    if (activePopup && activePopup !== 'datepicker' && datepickerPopupOpenState) {
      setDatepickerPopupOpenState(false);
    }
  }, [activePopup]);

  const NEXT_PUBLIC_PREFIX =
    process.env['NEXT_PUBLIC_PREFIX'] || useClientEnvVarsStore.getState().envVarsObject['NEXT_PUBLIC_PREFIX'];
  const queryCommonContext = {
    /**
     * define common params for all the query
     */
    context: {
      headers: {
        'x-request-id': `${Date.now()}`,
        'accept-language': currentLocale?.replace('_', '-'),
      },
    },
  };

  useEffect(() => {
    if (hideFlexibleDateFlow) {
      setCurrentActiveTab(tab1Label);
      if (flexible) {
        handleDateSelection(fromDate, toDate, false, false);
      }
    } else {
      setCurrentActiveTab(tab2Label);
    }
  }, [hideFlexibleDateFlow]);
  const callUpdateGlobalDate = (data: {
    advancedReservationDateLimit: { singleDateLimit: { value: any }; groupDateLimit: { value: any } };
  }) => {
    const initialSessionData = globalData?.sessionData ?? {};
    const initalCacheData = initialSessionData?.cacheData ?? {};
    const initalData = initalCacheData?.data ?? {};
    const initalAriesCommonData = initalData?.AriesCommon ?? {};
    const sessionData = {
      ...initialSessionData,
      cacheData: {
        ...initalCacheData,
        data: {
          ...initalData,
          AriesCommon: {
            ...initalAriesCommonData,
            singleDateLimit: data?.advancedReservationDateLimit?.singleDateLimit?.value,
            groupDateLimit: data?.advancedReservationDateLimit?.groupDateLimit?.value,
          },
        },
      },
    };
    updateGlobalData('sessionData', sessionData);
  };

  const [getInventoryDates] = useLazyQuery(phoenixShopAdvSearchInventoryDate, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted: data => {
      let singleDateLimit;
      let groupDateLimit;
      if (
        data?.advancedReservationDateLimit?.singleDateLimit?.value &&
        data?.advancedReservationDateLimit?.groupDateLimit?.value
      ) {
        singleDateLimit = data?.advancedReservationDateLimit?.singleDateLimit?.value;
        groupDateLimit = data?.advancedReservationDateLimit?.groupDateLimit?.value;
        updateSessionInSearchForm(
          globalData?.sessionData,
          useClientEnvVarsStore.getState().envVarsObject?.['SESSION_GET_CALL_URL_CLIENT'],
          NEXT_PUBLIC_PREFIX,
          singleDateLimit,
          groupDateLimit,
          globalData?.sessionData?.sessionToken
        );
        callUpdateGlobalDate({
          advancedReservationDateLimit: {
            singleDateLimit: {
              value: singleDateLimit,
            },
            groupDateLimit: {
              value: groupDateLimit,
            },
          },
        });
      } else {
        singleDateLimit = moment().add(DEFAULT_SINGLE_DATE_LIMIT, 'd');
        groupDateLimit = moment().add(DEFAULT_GROUP_DATE_LIMIT, 'd');
      }
      setSingleDateLimit(singleDateLimit);
    },
    onError: () => {
      const singleDateLimit = moment().add(DEFAULT_SINGLE_DATE_LIMIT, 'd');
      setSingleDateLimit(singleDateLimit);
    },
  });

  useEffect(() => {
    if (toDate && badgeValue() !== '') {
      setShowBadge(true);
    } else {
      setShowBadge(false);
    }
  }, [toDate, nightCount]);
  const [_totalNights, setTotalNights] = useState(0);
  const maxFlexibleNumberOfNights = maxNumberOfNights ? Number(maxNumberOfNights) : MAX_NUMBER_OF_NIGHTS_ALLOWED;

  // Tab label constants
  const TAB_LABEL_EXACT_DATE = 'exactDate';
  const TAB_LABEL_FLEXIBLE_DATE = 'flexibleDate';
  const tab1Label = TAB_LABEL_EXACT_DATE;
  const tab2Label = TAB_LABEL_FLEXIBLE_DATE;
  const [currentActiveTab, setCurrentActiveTab] = useState<string>(flexible ? tab2Label : tab1Label);
  // handle current active tab
  useEffect(() => {
    if (flexible) {
      setCurrentActiveTab(tab2Label);
    } else {
      setCurrentActiveTab(tab1Label);
    }
  }, [flexible]);
  const isDesktop = useCheckBreakpoint('viewportL');
  const isTabletView = useCheckBreakpoint('viewportM'); // check if viewport is for mobile, to change the calendar month
  const isMobileView = isMobileTestView ?? !isDesktop;
  const tabList = [
    {
      tabTitle: exactDatesTabText,
      tabSubtitle: '',
      tabValue: tab1Label,
      isActive: !flexible,
    },
  ];
  if (enableFlexibleSearch || flexibleDatesToggle) {
    tabList.push({
      tabTitle: flexibleTabText,
      tabSubtitle: '',
      tabValue: tab2Label,
      isActive: flexible,
    });
  }

  /**
   * Validates and normalizes locale data with fallbacks
   * These values update as per locale - we get week days information from AEM
   * Updates AEM value and fallback if invalid
   */
  const validateAndNormalizeLocaleData = () => {
    const validateArrayLength = <T,>(value: string | undefined, expectedLength: number, fallback: T): T => {
      const split = value?.split(',');
      return (split?.length === expectedLength ? split : fallback) as T;
    };

    return {
      shortWeekdays: validateArrayLength(weekdays, 7, SHORT_WEEK_DAYS),
      weekdaysShort: validateArrayLength(weekdaysShort, 7, SHORT_WEEK_DAYS_NAME),
      weekdaysLong: validateArrayLength(weekdaysLong, 7, LONG_WEEK_DAYS_NAME),
      months: validateArrayLength(months, 12, LocaleUtils.getMonths()),
      monthsShort: validateArrayLength(monthsShort, 12, SHORT_MONTH_NAME),
    };
  };

  const {
    shortWeekdays,
    weekdaysShort: weekdaysShortValue,
    weekdaysLong: weekdaysLongName,
    months: monthsValue,
    monthsShort: monthsShortValue,
  } = validateAndNormalizeLocaleData();

  const setLanguageLocalization = (): void => {
    /**
     * localization for moment to get the date formatted
     */
    moment.locale('locale', {
      months: monthsValue,
      monthsShort: monthsShortValue,
      weekdays: weekdaysLongName,
      weekdaysShort: weekdaysShortValue,
    });
  };

  // Sync isModalOpen prop with internal state when controlled
  useEffect(() => {
    if (isModalOpen !== undefined) {
      setDatepickerPopupOpenState(isModalOpen);
    }
  }, [isModalOpen]);

  // Track whether this DatePicker instance set body overflow
  const didSetOverflowRef = useRef(false);

  useEffect(() => {
    // When inside a parent modal, don't manage body overflow at all
    // The parent modal is responsible for managing body overflow
    if (isInModal) {
      return; // No cleanup function registered
    }

    // Only manage overflow if we're actually showing the DatePicker modal
    if (isMobileView && datepickerPopupOpenState) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
      didSetOverflowRef.current = true;
      inputElementRef.current?.focus();
    }

    return () => {
      // Only reset overflow if THIS instance set it
      if (didSetOverflowRef.current) {
        document.getElementsByTagName('body')[0].style.overflow = 'auto';
        didSetOverflowRef.current = false;
      }
    };
  }, [datepickerPopupOpenState, isMobileView, isInModal]);

  // effect to handle the changes after popup state is set to open

  useEffect(() => {
    /**
     * setLocalization on moment object
     */
    setLanguageLocalization();

    if (!setDefaultDate) {
      resetButtonHandler();
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClientSideView(true);
    }
    if (!singleDateLimit && !skipQuery) {
      getInventoryDates({
        ...queryCommonContext,
      });
    }
  }, []);

  //get the lang from document in client side
  const getDocumentLang = () => {
    if (typeof document === 'undefined') return null;
    return document.documentElement?.lang?.replace('_', '-');
  };

  /**
   * Formats exact date range based on whether dates are in the same month/year
   * @param fromDate - Start date (moment object or string)
   * @param toDate - End date (moment object or string)
   * @param locale - Current locale string
   * @returns Formatted date string: "Mon D - D" if same month/year, "Mon D - Mon D" if different months
   *          (Mon = shortened month name like "Jan", "Feb", D = day without leading zeros)
   */
  const formatExactDateRange = (
    fromDate: DateObjectType | null | undefined,
    toDate: DateObjectType | null | undefined,
    locale: string | undefined
  ): string => {
    if (!fromDate || !toDate) {
      return '';
    }

    // Ensure dates are moment objects (handle both moment objects and strings)
    const fromMoment = moment(fromDate);
    const toMoment = moment(toDate);
    const isSameMonthYear = fromMoment.month() === toMoment.month() && fromMoment.year() === toMoment.year();

    // Use 'Do' (ordinal day) for Chinese locales to get "5æ—¥" format, 'D' for others (no leading zeros)
    const isChineseLocale = isCNLocale(locale || '') || isGreaterChinaLocale(locale || '');
    const dayFormat = isChineseLocale ? 'Do' : 'D';
    const monthDayFormat = isChineseLocale ? 'MMM Do' : 'MMM D';

    if (isSameMonthYear) {
      // Format: "Mon D - D" (e.g., "Jan 15 - 20" or "Feb 3 - 4" or "2æœˆ 5æ—¥ - 6æ—¥")
      const startDateFormatted = getCustomFormattedDate(
        getLocalizedDate(fromDate, monthDayFormat, locale),
        locale || ''
      );
      const endDay = getLocalizedDate(toDate, dayFormat, locale);
      return `${startDateFormatted} - ${endDay}`;
    } else {
      // Format: "Mon D - Mon D" (e.g., "Jan 15 - Feb 20" or "Feb 3 - Mar 4" or "2æœˆ 5æ—¥ - 3æœˆ 6æ—¥")
      const startDateFormatted = getCustomFormattedDate(
        getLocalizedDate(fromDate, monthDayFormat, locale),
        locale || ''
      );
      const endDateFormatted = getCustomFormattedDate(getLocalizedDate(toDate, monthDayFormat, locale), locale || '');
      return `${startDateFormatted} - ${endDateFormatted}`;
    }
  };

  // if from and to dates not available then show 'Check in - Check out' else "fromDate - toDate"
  let displayText = '';
  const isFlexibleMode = flexible || currentActiveTab === tab2Label;
  if ((fromDate || toDate) && !isFlexibleMode) {
    if (fromDate && toDate) {
      // Both dates selected - show range format
      displayText = formatExactDateRange(fromDate, toDate, currentLocale);
    } else if (fromDate) {
      // Only start date selected - show "Mon D" format (no leading zeros)
      const isChineseLocale = isCNLocale(currentLocale || '') || isGreaterChinaLocale(currentLocale || '');
      const monthDayFormat = isChineseLocale ? 'MMM Do' : 'MMM D';
      displayText =
        getCustomFormattedDate(getLocalizedDate(fromDate, monthDayFormat, currentLocale), currentLocale || '') + ' - ';
    } else {
      displayText = '';
    }
  } else {
    // Flexible dates mode - show month and year (e.g., "February 2026")
    displayText = fromDate
      ? convertToUpperCase(
          getLocalizedDate(
            fromDate,
            isGreaterChinaLocale(currentLocale)
              ? CN_FLEXIBLE_FORMAT_YEAR_MONTH
              : flexibleDateFormatLabel ?? FLEXIBLE_FORMAT_YEAR_MONTH,
            currentLocale
          ),
          currentLocale
        )
      : '';
  }

  if ((currentLocale || getDocumentLang()) === TH_LOCALE) {
    displayText = modifyDateStringForThai(displayText);
  }

  displayText = formatDateStringBasedOnLocale(displayText);

  const resetButtonHandler = () => {
    const from = getCurrentDateObject();
    const end = getNextDateObject(from);
    handleDateSelection(from, end, flexible, true);
    /**
     * Set the flag to true on reset btn click
     */
    setResetFlag(true);
    setTotalNights(generateRandomNumber());
  };

  const getTotalNumberOfNights = (from: DateObjectType, end: DateObjectType, flexi = false) => {
    /**
     * in case of flexible it will return the max number of nights
     */
    const totalnNights = getTotalNumberOfDays(from, end);
    setTotalNights(totalnNights);
    if (totalnNights > maxFlexibleNumberOfNights && flexi) {
      //  return if user has enterd max number of nights in case of flexible
      return true;
    }
    // other cases return false
    return false;
  };

  /**
   * Builds the date state object for the search form store
   */
  const buildDateState = (
    from: moment.Moment | null | undefined,
    end: moment.Moment | null | undefined,
    flexible: boolean,
    isReset: boolean
  ) => {
    if (isReset) {
      return {
        fromDate: null,
        toDate: null,
        flexible,
        checkInDate: '',
        checkOutDate: '',
        numberOfNights: 0,
        lengthOfStay: 0,
      };
    }

    const hasValidDates = from && end && !getTotalNumberOfNights(from, end, flexible);
    const totalNights = hasValidDates ? Number(getTotalNumberOfDays(from, end)?.toFixed(0) || 1) : 1;
    const lengthOfStay = hasValidDates ? getTotalNumberOfDays(from, end) : 1;

    return {
      fromDate: from,
      toDate: end,
      flexible,
      checkInDate: from && !flexible ? getValueFromDate(from) : '',
      checkOutDate: end && !flexible ? getValueFromDate(end) : '',
      numberOfNights: totalNights,
      lengthOfStay,
    };
  };

  const handleDateSelection = (
    startDate: DateObjectType | null | undefined,
    endDate: DateObjectType | null | undefined,
    flexible = false,
    isReset = false
  ) => {
    /**
     * handle date selection
     */
    const today = getCurrentDateObject();
    let from: moment.Moment | null | undefined = startDate ?? today;
    let end: moment.Moment | null | undefined = endDate ?? null;

    if (from && end && getTotalNumberOfNights(from, end, flexible)) {
      // reset date in case of flexible if we have more than max nights
      const date = from.startOf('month');
      from = date.isBefore(today) ? today : date;
      end = getNextDateObject(from);
    }

    if (end?.isBefore(from)) {
      end = null;
    }

    const dateState = buildDateState(from, end, flexible, isReset && !flexible);

    setSearchFormState([DATES], {
      ...dates,
      [DATES]: {
        ...dates,
        ...dateState,
      },
    });

    // Handle icon visibility for flexible mode
    if (flexible && !isReset) {
      const total = getTotalNumberOfDays(from, end);
      const month = moment(from).month();
      const year = moment(from).year();
      const isCurrentMonth = total === 1 && month === moment().month() && year === moment().year();
      setShowIcon(!isCurrentMonth);
    } else {
      setShowIcon(!isReset);
    }
  };

  const onTabChange = (currTab: string): void => {
    /**
     * handle tab change on datepicker
     */
    if (currTab === tab2Label) {
      // reset dates in case of flexible if user enter more then max limit val
      handleDateSelection(fromDate, toDate, true);
    } else {
      handleDateSelection(fromDate, toDate, false);
    }
    setCurrentActiveTab(currTab);
  };

  const badgeValue = () => {
    if (nightCount > 0) {
      return `${nightCount === 1 ? `${nightCount} ${nightLabel}` : `${nightCount} ${nights}`}`;
    } else {
      return;
    }
  };

  const handleModalClose = (): void => {
    /**
     * close modal on done click
     * handle all other required part when user close the date picker modal
     */
    if (toDate === null && fromDate !== null && fromDate !== undefined) {
      handleDateSelection(fromDate, getNextDateObject(fromDate), flexible);
    }
    setDatepickerPopupOpenState(false);
    if (activePopup === 'datepicker') {
      setActivePopup(null);
    }
    setShowIcon(false);

    if (onDatePickerChange) {
      onDatePickerChange(!!toDate);
    }
  };

  useEffect(() => {
    if (datepickerPopupOpenState === false && !toDate && fromDate) {
      handleDateSelection(fromDate, getNextDateObject(fromDate), flexible);
    }
  }, [datepickerPopupOpenState]);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event?.target as Element;

      // Ignore clicks on external modals (privacy popup, Adobe experiments, etc.)
      if (shouldIgnoreClickOutside(target)) {
        return;
      }

      // Check if the click target is not within the popup or the button or the icons
      if (!target.closest('.shop-datepicker-modal') && !target.closest('.datepicker-input-container')) {
        setDatepickerPopupOpenState(false);
        if (activePopup === 'datepicker') {
          setActivePopup(null);
        }
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [datepickerPopupOpenState, activePopup]);

  useEffect(() => {
    // close datepicker when esc key is pressed
    const handleEscKey = (event: { key: string; shiftKey: any; preventDefault: () => void }) => {
      if (event.key === 'Escape') {
        onDatePickerCancel?.();
        setDatepickerPopupOpenState(false);
        if (activePopup === 'datepicker') {
          setActivePopup(null);
        }
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('keydown', handleEscKey);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [datepickerPopupOpenState, activePopup]);

  /**
   *
   * @returns
   * component that will render inside the tab based one the current active tab
   */
  const calendarComp = () => {
    if (currentActiveTab === tab1Label) {
      return (
        <div
          className={clsx(
            hideFlexibleDateFlow && !isDesktop ? 'datepicker-calendar-wrapper-mobile' : 'datepicker-calendar-wrapper',
            !hideFlexibleDateFlow ? 'datepicker-calendar-wrapper-multi-tabs' : ''
          )}
        >
          <DatePickerExact
            weekdays={shortWeekdays}
            months={monthsValue}
            weekdaysLong={weekdaysLongName}
            monthsShort={monthsShortValue}
            weekdaysShort={weekdaysShortValue}
            isMobile={isMobileView}
            onDateSelection={handleDateSelection}
            dates={{ ...dates }}
            resetFlag={resetFlag}
            setResetFlag={setResetFlag}
            isTabletView={isTabletView}
            isDatePickerModalOpen={datepickerPopupOpenState}
            clickTrackingLoc={clickTrackingLoc}
            singleDateLimit={singleDateLimit}
            isWeekendButtonSelected={isWeekendButtonSelected}
            setIsWeekendButtonSelected={setIsWeekendButtonSelected}
            enableWeekendSelectorOnMobile={enableWeekendSelectorOnMobile}
          />
        </div>
      );
    } else {
      return (
        <DatePickerFlexible
          clickTrackingLoc={clickTrackingLoc}
          dates={dates}
          onDateSelection={handleDateSelection}
          monthList={monthsValue}
          singleDateLimit={singleDateLimit}
        />
      );
    }
  };
  const buttonRef = useRef<HTMLButtonElement>(null); //ref to the button of datepicker , to be used in dropdown modal to identify the last element of the modal

  const FooterComponent: FC = () => (
    <StyledFooterDatePickerDiv
      className={clsx(
        'datepicker-footer align-items-center d-flex justify-content-between  standard',
        'pt-3',
        !isDesktop ? 'pb-4 px-4' : '',
        isDesktop && flexible && 'pt-5 mt-5'
      )}
    >
      <a
        href="#"
        className="m-link-action reset-action-button custom_click_track"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          resetButtonHandler();
        }}
        title={resetSpecificDateCta}
        data-testid={resetSpecificDateCta}
        custom_click_track_value={`${clickTrackingLoc}| ${
          currentActiveTab === tab2Label ? FLEXIBLE_DATES : SPECIFIC_DATES
        } ${datepickerResetClickTrackingLoc} |internal`}
        role="button" // forcefully setting role = button to anchor tag as it was reading Reset Link1
      >
        {resetSpecificDateCta}
      </a>
      <Button
        ref={buttonRef}
        callback={handleModalClose}
        className="m-button-secondary m-button-s"
        custom_click_track_value={`${clickTrackingLoc}| ${submitSpecificDateCta} button |internal`}
        {...(applyTrackLabel && { 'data-track-label': applyTrackLabel })}
        buttonCopy={
          currentActiveTab === tab1Label
            ? submitSpecificDateCta
            : isDesktop
            ? flexibleDatesSubmitCta
            : submitSpecificDateCtaMobile
        }
      />
    </StyledFooterDatePickerDiv>
  );
  const datepickerref = useRef<HTMLDivElement>(null);
  const inputElementRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null); //ref to point parent div of inputTextField

  // function to close dropdown when focus leaves both input and dropdown
  const handleBlur = (event: any) => {
    const relatedTarget = event.relatedTarget as Element;
    // Don't close if focus moved to an external modal (privacy popup, Adobe experiments, etc.)
    if (shouldIgnoreFocusOutside(relatedTarget)) {
      return;
    }
    if (
      relatedTarget &&
      !inputRef?.current?.contains(relatedTarget) &&
      !datepickerref.current?.contains(relatedTarget)
    ) {
      setDatepickerPopupOpenState(false);
      if (activePopup === 'datepicker') {
        setActivePopup(null);
      }
    }
  };

  return (
    <StyledDatePickerDiv
      data-component-name="m-shop-DatePicker"
      data-testid="shop-DatePicker"
      tabIndex={-1}
      $isRepositionNoOfNightsRequired={currentLocale === EL_LOCALE || getDocumentLang() === EL_LOCALE}
      $isNextGenHP={isNextGenHP}
      className={clsx(
        isMobileView ? 'date-picker-mobile-wrapper' : '',
        'date-picker-wrapper',
        // For NextGen standard:
        //   - Desktop: consider isSearchFormActiveState or datepickerPopupOpenState
        //   - Mobile inside modal: always active (modal = editing mode)
        //   - Mobile outside modal: always inactive
        // For NextGenHP: only consider datepickerPopupOpenState
        !isNextGenHP
          ? isDesktop
            ? datepickerPopupOpenState || isSearchFormActiveState
              ? 'is-active'
              : 'is-inactive'
            : isInModal
            ? 'is-active' // Mobile inside modal = always active
            : 'is-inactive' // Mobile outside modal = always inactive
          : datepickerPopupOpenState
          ? 'is-active'
          : 'is-inactive'
      )}
      ref={inputRef}
    >
      {!hideInput &&
      (() => {
        // Show input if: mobile form, desktop view, or mobile view with closed modal
        const shouldShowInput = isMobileForm || isDesktop;
        const isMobileModalClosed =
          isMobileView && (isModalOpen !== undefined ? !isModalOpen : !datepickerPopupOpenState);
        return shouldShowInput || isMobileModalClosed;
      })() ? (
        <div
          className={clsx(
            'date-input-field-wrapper',
            'standard',
            'date-cursor',
            isMobileForm && isNextGenHP ? 'dates-input-field' : '',
            inputFieldWrapperClassName
          )}
          data-test-id="date-input-modal-button"
          onClick={() => {
            //removed event.stopPropagation as it was restricting Analytics tracking
            onInputClick?.();
            // For desktop NextGen standard: prevent opening modal on first click when inactive
            // Only activate the field on first click, modal opens on second click
            const shouldPreventModalOpen =
              isDesktop && !isNextGenHP && !isSearchFormActiveState && !datepickerPopupOpenState;

            // Only update local state if not controlled by parent via isModalOpen prop
            if (isModalOpen === undefined) {
              onInteraction?.();
              if (!shouldPreventModalOpen) {
                const newState = !datepickerPopupOpenState;
                setDatepickerPopupOpenState(newState);
                if (newState) {
                  setActivePopup('datepicker');
                } else if (activePopup === 'datepicker') {
                  setActivePopup(null);
                }
              }
            }
          }}
          tabIndex={-1}
          onKeyDown={(ev: KeyboardEvent) => {
            if (ev.keyCode === 13) {
              // Handle inactive state: First Enter should activate the form
              if (!isSearchFormActiveState && !isNextGenHP && isDesktop) {
                ev.preventDefault();
                setIsSearchFormActiveState(true);
                // Focus will remain on the same input after state change
                setTimeout(() => {
                  // Use the event target directly - it should be the input that received the keypress
                  const dateInput = ev.target as HTMLInputElement;
                  if (dateInput && dateInput.tagName === 'INPUT') {
                    dateInput.focus();
                  }
                }, 100);
                return;
              }

              // check if user press enter on datepicker
              onInputClick?.();
              // For desktop NextGen standard: prevent opening modal on first enter when inactive
              // Only activate the field on first enter, modal opens on second enter
              const shouldPreventModalOpen =
                isDesktop && !isNextGenHP && !isSearchFormActiveState && !datepickerPopupOpenState;

              // Only toggle local state if not controlled by parent via isModalOpen prop
              if (isModalOpen === undefined) {
                onInteraction?.();
                if (!shouldPreventModalOpen) {
                  const newState = !datepickerPopupOpenState;
                  setDatepickerPopupOpenState(newState);
                  if (newState) {
                    setActivePopup('datepicker');
                  } else if (activePopup === 'datepicker') {
                    setActivePopup(null);
                  }
                }
              }
            }
          }}
        >
          {/* Show icon for NextGen standard inactive state (desktop or mobile outside modal) */}
          {!datepickerPopupOpenState && !isNextGenHP && (isDesktop ? !isSearchFormActiveState : !isInModal) && (
            <span className="datepicker-icon-wrapper">
              <Icon iconClass="icon-stay-dates" />
            </span>
          )}
          <div
            className="datepicker-input-ch-wrapper"
            style={
              // Apply pixel-width for NextGen standard inactive state (desktop or mobile outside modal)
              // Only measure on client after mount to avoid SSR hydration mismatch
              !datepickerPopupOpenState &&
              !isNextGenHP &&
              (isDesktop ? !isSearchFormActiveState : !isInModal) &&
              displayText &&
              isClientMounted
                ? ({
                    '--input-ch-width': `${measureTextWidth(displayText) + (isFlexibleMode ? 16 : 8)}px`,
                  } as React.CSSProperties)
                : undefined
            }
          >
            <InputTextField
              onBlur={handleBlur}
              className={'m-input-text-field datepicker-input-container'} // these 2 class names are mandatory , please do not remove
              classNameForLineType={isMobileForm && isNextGenHP ? 'mobile-homepage-date-field' : ''}
              label={
                datepickerPopupOpenState ||
                inputFieldWrapperClassName === 'main-mobile-modal-datepicker-field' ||
                isNextGenHP ||
                (!isNextGenHP && isSearchFormActiveState)
                  ? stayDates
                  : undefined
              }
              inputValue={isClientSideView ? displayText : ''}
              getInputProps={() => ({
                readOnly: true,
              })}
              onFocus={() => {
                if (displayText.length && !flexible) {
                  setShowIcon(true);
                }
                onInteraction?.();
              }}
              inputTextFieldClassName={clsx('custom_click_track', 'datepicker-input-container')}
              placeHolderText={isMobileForm ? dateHelperTextMobile : dateHelperText}
              togglePlaceholder={true}
              iconForLabel={!isNextGenHP ? 'icon-stay-dates' : undefined}
              custom_click_track_value={`${searchFormClickTrackingLoc} | Date picker selected |internal}`}
              icon_custom_click_track_value={`${searchFormClickTrackingLoc} | Cleared dates |internal}`}
              infoLabel={
                displayText && nightCount > 0
                  ? `date-picker, ${displayText}, ${
                      nightCount === 1 ? `${nightCount} ${nightLabel}` : `${nightCount} ${nights}`
                    }`
                  : 'date-picker'
              }
              variation="default"
              withinModal={false}
              showUnderline={isMobileForm && isNextGenHP}
              showBadge={
                (showBadge && datepickerPopupOpenState) ||
                inputFieldWrapperClassName === 'main-mobile-modal-datepicker-field' ||
                (showBadge && isNextGenHP) ||
                (showBadge && !isNextGenHP && isSearchFormActiveState)
              }
              badgeValue={badgeValue()}
              readOnly={true}
            />
          </div>
          {/* Show nights text only for NextGen standard inactive state (desktop inactive or mobile outside modal) */}
          {!datepickerPopupOpenState &&
            nightCount > 0 &&
            !isNextGenHP &&
            (isDesktop ? !isSearchFormActiveState : !isInModal) && (
              <span className="datepicker-nights-text-inactive">
                ({nightCount === 1 ? `${nightCount} ${nightLabel}` : `${nightCount} ${nights}`})
              </span>
            )}
        </div>
      ) : null}
      <div ref={datepickerref} className="datepicker-dropdown-wrapper">
        {(isModalOpen !== undefined ? isModalOpen : datepickerPopupOpenState) && (
          <DropDownModal
            id={modalId}
            callhandleBlur={handleBlur}
            labelledBy={modalId ? `${modalId}-content` : 'date-picker-modal'}
            shouldAutoFocusOnPopup={true}
            isAutoScrollEnabled={isNextGenHP ? true : false}
            parentElementTopValue={searchFormTopPosition?.value}
            handleBlur={false}
            scrollDownModalCloseState={true}
            dropDownSpecificFlag={true}
            applyDefaultHeight={isNextGenHP ? true : false}
            className={clsx(
              'shop-datepicker-modal standard',
              isSearchFormSticky && isCNLocale(currentLocale) && 'is-search-form-sticky',
              alignRight && 'align-right'
            )}
            dropDownChildrenClassName={flexible ? 'shop-dropdown-children-el' : ''}
            childrenClassName={clsx(
              'shop-datepicker-dropdown',
              datepickerPopupOpenState && isDesktop ? 'shop-datepicker-dropdown-styles' : '',
              {
                'shop-date-picker-drop-down-cn': isCNLocale(currentLocale),
              }
            )}
            show={isModalOpen !== undefined ? isModalOpen : datepickerPopupOpenState}
            dropdownModalOpenState={isModalOpen !== undefined ? isModalOpen : datepickerPopupOpenState}
            setDropdownModalOpenState={val => {
              if (toDate === null && fromDate !== null && fromDate !== undefined) {
                handleDateSelection(fromDate, getNextDateObject(fromDate), flexible);
              }

              /**
               * Note - The problem here is that the variable 'resetFlag' is undefined inside the callback.
               * The solution is a workaround to access latest state via 'prevState' arg.
               **/

              let latestResetFlag;
              setResetFlag(prev => {
                latestResetFlag = prev;
                return prev;
              });
              if (!(latestResetFlag && showIcon)) {
                setDatepickerPopupOpenState(val);
              }
            }}
            // mobile setting enabled
            mobileTakeOver={isMobileView}
            mobileModalHeading={dateModalHeader}
            mobileHeaderEnabled={isMobileView}
            dropdownModalOnCLoseFunc={() => {
              setShowIcon(false);

              setDatepickerPopupOpenState(false); //toggle datepicker on enter
              if (activePopup === 'datepicker') {
                setActivePopup(null);
              }
              if (onDatePickerCancel) {
                onDatePickerCancel();
              }
            }}
            customLastElementRef={buttonRef}
            role="dialog"
          >
            <>
              <div className={clsx('modal-header-sticky')}>
                {/* appears within the modal itself */}
                {isMobileView && (
                  <div className={clsx('modal-header-input', 'standard', mobileModalInputClassName)}>
                    <InputTextField
                      ref={inputElementRef}
                      onFocus={() => {
                        if (displayText.length && !flexible) {
                          setShowIcon(true);
                        }
                      }}
                      className={'m-input-text-field datepicker-input-container mobile'} // these 2 class names are mandatory , please do not remove
                      label={stayDates}
                      inputValue={displayText}
                      getInputProps={() => ({
                        readOnly: true,
                      })}
                      placeHolderText={dateHelperTextMobile}
                      togglePlaceholder={true}
                      showIcon={datepickerPopupOpenState && showIcon && !showBadge}
                      iconForLabel={!isNextGenHP ? 'icon-stay-dates' : undefined}
                      infoLabel={
                        displayText && nightCount > 0
                          ? `Date Picker, ${displayText}, ${
                              nightCount === 1 ? `${nightCount} ${nightLabel}` : `${nightCount} ${nights}`
                            }`
                          : 'Date Picker'
                      }
                      variation="default"
                      withinModal={true}
                      showUnderline={true}
                      custom_click_track_value={`${searchFormClickTrackingLoc} | Date picker selected |internal`}
                      icon_custom_click_track_value={`${searchFormClickTrackingLoc} | Cleared dates |internal`}
                      showBadge={showBadge || isNextGenHP}
                      badgeValue={badgeValue()}
                      readOnly={true}
                    />
                  </div>
                )}
                <TabComponent
                  tabList={tabList} // pass the tab content
                  setSelectedTab={onTabChange}
                  selectedTab={currentActiveTab}
                  clickTrackingLoc={clickTrackingLoc} // tab click value
                  hidetabsAndContent={false}
                  children={calendarComp()}
                  isSwitcherTabs={true} // second varitaion of tab
                  customClass={
                    currentActiveTab === tab1Label && !isDesktop
                      ? 'datepicker-tab-wrapper-exact'
                      : 'datepicker-tab-wrapper'
                  }
                  disabledContainerClass={true}
                  hideTabs={hideTabHeader}
                  ariaRole="button"
                />
              </div>
              <FooterComponent />
            </>
          </DropDownModal>
        )}
      </div>
    </StyledDatePickerDiv>
  );
};

export default NGDatePicker;
